import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  useGetAiTemplatesQuery,
  useGetAiListingsQuery,
  useCreateAiListingMutation,
  useUpdateAiListingMutation,
  useDeleteAiListingMutation,
  useUnlistAiListingMutation,
  useRelistAiListingMutation,
} from "@/store/admin_api";
import { usePostFileMutation } from "@/store/rest_api";
import { AiAgentListing, AiListingVisibility } from "@/model/interface/AiAgent";
import Organisation from "@/model/interface/Organisation";
import OrganisationCombobox from "@/components/pages/platform-admin/organisations/OrganisationCombobox";
import FileResponse from "@/model/interface/FileResponse";
import { apiErrorMessage } from "@/util/apiError";
import { Loader2, X } from "lucide-react";
import React from "react";

const emptyListing: Partial<AiAgentListing> = {
  longDescription: "",
  perks: [],
  limits: [],
  media: null,
  monthlyFee: 0,
  discountPercent: 0,
  perMinuteFee: 0,
  visibility: "PUBLIC",
  allowedOrganisationSummaries: [],
  featured: false,
  sortOrder: 0,
};

interface ListEditorProps {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
}

function ListEditor({ label, placeholder, values, onChange }: ListEditorProps) {
  const [draft, setDraft] = useState("");

  const addDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {values.length > 0 && (
        <ul className="space-y-1">
          {values.map((value, index) => (
            <li
              key={`${value}-${index}`}
              className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
            >
              <span>{value}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => onChange(values.filter((_, candidateIndex) => candidateIndex !== index))}
              >
                <X className="h-3 w-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addDraft();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={addDraft}>
          Add
        </Button>
      </div>
    </div>
  );
}

const LISTED_FILTERS = [
  { value: "ALL", label: "All listings" },
  { value: "LIVE", label: "Live" },
  { value: "UNLISTED", label: "Unlisted" },
];

export default function AiListingsAdmin() {
  const [listedFilter, setListedFilter] = useState<string>("ALL");
  const [page, setPage] = useState(0);
  const { data: listingPage, isLoading } = useGetAiListingsQuery({
    listed: listedFilter === "ALL" ? undefined : listedFilter === "LIVE",
    page,
    size: 25,
  });
  const { data: templatePage } = useGetAiTemplatesQuery({ status: "PUBLISHED", size: 200 });
  const [createListing] = useCreateAiListingMutation();
  const [updateListing] = useUpdateAiListingMutation();
  const [deleteListing] = useDeleteAiListingMutation();
  const [unlistListing] = useUnlistAiListingMutation();
  const [relistListing] = useRelistAiListingMutation();
  const [uploadMediaFile, { isLoading: isUploadingMedia }] = usePostFileMutation();
  const mediaInputRef = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState<Partial<AiAgentListing> | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const openCreate = () => {
    setEditing({ ...emptyListing });
    setSelectedTemplateId("");
    setDialogOpen(true);
  };

  const openEdit = (listing: AiAgentListing) => {
    setEditing({ ...listing });
    setSelectedTemplateId(listing.template?.id ?? "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedTemplateId) {
      toast.error("Please select a template");
      return;
    }
    const isPrivateListing = editing?.visibility === "PRIVATE";
    if (isPrivateListing && (editing?.allowedOrganisationSummaries ?? []).length === 0) {
      toast.error("Add at least one organisation, or make the listing public");
      return;
    }
    try {
      const payload = {
        ...editing,
        template: { id: selectedTemplateId },
        media: editing?.media ? { id: editing.media.id } : null,
        allowedOrganisationIds: isPrivateListing
          ? (editing?.allowedOrganisationSummaries ?? []).map((organisation) => organisation.id)
          : [],
      };
      if (editing?.id) {
        await updateListing({ id: editing.id, data: payload }).unwrap();
        toast.success("Listing updated");
      } else {
        await createListing(payload).unwrap();
        toast.success("Listing created");
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save"));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteListing(id).unwrap();
      toast.success("Listing deleted");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete"));
    }
  };

  const handleToggleListed = async (listing: AiAgentListing) => {
    try {
      if (listing.unlistedAt) {
        await relistListing(listing.id).unwrap();
        toast.success("Listing is back in the store");
      } else {
        await unlistListing(listing.id).unwrap();
        toast.success("Listing removed from the store");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update listing"));
    }
  };

  const setField = (field: string, value: any) => setEditing((prev) => (prev ? { ...prev, [field]: value } : prev));

  const addAllowedOrganisation = (organisation: Organisation | null) => {
    if (!organisation) {
      return;
    }
    const currentOrganisations = editing?.allowedOrganisationSummaries ?? [];
    if (currentOrganisations.some((entry) => entry.id === organisation.id)) {
      return;
    }
    setField("allowedOrganisationSummaries", [...currentOrganisations, { id: organisation.id, name: organisation.name }]);
  };

  const removeAllowedOrganisation = (organisationId: string) => {
    setField(
      "allowedOrganisationSummaries",
      (editing?.allowedOrganisationSummaries ?? []).filter((entry) => entry.id !== organisationId),
    );
  };

  const handleMediaSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    try {
      const result = await uploadMediaFile({ file }).unwrap();
      const uploaded = (Array.isArray(result) ? result[0] : result) as FileResponse | undefined;
      if (!uploaded?.id) {
        toast.error("Failed to upload media");
        return;
      }
      setField("media", { id: uploaded.id, url: uploaded.url, contentType: file.type });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to upload media"));
    }
  };

  const publishedTemplates = templatePage?.items ?? [];
  const listings = listingPage?.items ?? [];
  const totalPages = listingPage?.totalPages ?? 0;

  const changeFilter = (nextFilter: string) => {
    setListedFilter(nextFilter);
    setPage(0);
  };

  useEffect(() => {
    const requestedTemplateId = searchParams.get("template");
    if (!requestedTemplateId) {
      return;
    }
    setEditing({ ...emptyListing });
    setSelectedTemplateId(requestedTemplateId);
    setDialogOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Marketplace Listings</h3>
          <p className="text-sm text-muted-foreground">Publish agent templates to the marketplace for organisations to acquire.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={listedFilter} onValueChange={changeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LISTED_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>{filter.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} disabled={publishedTemplates.length === 0}>New Listing</Button>
        </div>
      </div>

      {publishedTemplates.length === 0 && (
        <div className="text-sm text-muted-foreground border rounded-lg p-4">
          No published templates available. Create and publish a template first.
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agent</TableHead>
            <TableHead>Visibility</TableHead>
            <TableHead>Monthly</TableHead>
            <TableHead>Acquisitions</TableHead>
            <TableHead>Featured</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listings.map((listing) => (
            <TableRow key={listing.id} className="cursor-pointer" onClick={() => openEdit(listing)}>
              <TableCell>
                <div className="font-medium">{listing.displayName || listing.template?.name || "Unknown"}</div>
                <div className="text-xs text-muted-foreground">{listing.template?.category}</div>
              </TableCell>
              <TableCell>
                {listing.visibility === "PRIVATE" ? (
                  <Badge variant="outline">
                    Private · {(listing.allowedOrganisationSummaries ?? []).length}{" "}
                    {(listing.allowedOrganisationSummaries ?? []).length === 1 ? "org" : "orgs"}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">Public</span>
                )}
              </TableCell>
              <TableCell>
                {listing.visibility === "PRIVATE" ? (
                  <>
                    {listing.monthlyFee > 0 ? `$${listing.monthlyFee}/mo` : "Free"}
                    {listing.discountPercent > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">{listing.discountPercent}% off</span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">Tier price</span>
                )}
              </TableCell>
              <TableCell>{listing.acquisitionCount}</TableCell>
              <TableCell>
                {listing.featured && <Badge>Featured</Badge>}
              </TableCell>
              <TableCell>
                {listing.unlistedAt ? <Badge variant="outline">Unlisted</Badge> : <Badge variant="secondary">Live</Badge>}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleListed(listing);
                  }}
                >
                  {listing.unlistedAt ? "Relist" : "Unlist"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(listing.id);
                  }}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {listings.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">No listings</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={listingPage?.last ?? true} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogTitle>{editing?.id ? "Edit" : "New"} Marketplace Listing</DialogTitle>
          <DialogDescription>Configure pricing and visibility for this agent in the marketplace.</DialogDescription>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>Template *</Label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger><SelectValue placeholder="Select a published template" /></SelectTrigger>
                  <SelectContent>
                    {publishedTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>{template.name} ({template.category})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Display Name</Label>
                <Input
                  value={editing.displayName ?? ""}
                  onChange={(event) => setField("displayName", event.target.value)}
                  placeholder="Leave empty to use the template's name"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Long Description</Label>
                <Textarea
                  value={editing.longDescription ?? ""}
                  onChange={(event) => setField("longDescription", event.target.value)}
                  placeholder="Detailed description for the marketplace listing page..."
                  rows={4}
                />
              </div>

              <ListEditor
                label="Perks (what it does)"
                placeholder="e.g., Answers every call around the clock"
                values={editing.perks ?? []}
                onChange={(values) => setField("perks", values)}
              />

              <ListEditor
                label="Limits (what it won't do)"
                placeholder="e.g., Does not take payments over the phone"
                values={editing.limits ?? []}
                onChange={(values) => setField("limits", values)}
              />

              <div className="space-y-1.5">
                <Label>Image or Video</Label>
                {editing.media?.url ? (
                  editing.media.contentType?.startsWith("video") ? (
                    <video src={editing.media.url} controls className="w-full max-h-64 rounded-md border" />
                  ) : (
                    <img
                      src={editing.media.url}
                      alt="Listing media"
                      className="w-full max-h-64 rounded-md border object-cover"
                    />
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">Shown at the top of the agent page in the marketplace.</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => mediaInputRef.current?.click()}
                    disabled={isUploadingMedia}
                  >
                    {isUploadingMedia && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {editing.media ? "Replace" : "Upload"}
                  </Button>
                  {editing.media && (
                    <Button type="button" variant="ghost" onClick={() => setField("media", null)}>
                      Remove
                    </Button>
                  )}
                </div>
                <input
                  ref={mediaInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleMediaSelected}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Who can see it</Label>
                <Select
                  value={editing.visibility ?? "PUBLIC"}
                  onValueChange={(nextVisibility) => setField("visibility", nextVisibility as AiListingVisibility)}
                >
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLIC">Public: every organisation</SelectItem>
                    <SelectItem value="PRIVATE">Private: only the organisations below</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editing.visibility === "PRIVATE" ? (
                <>
                  <div className="space-y-1.5">
                    <Label>Organisations *</Label>
                    <OrganisationCombobox value={null} onChange={addAllowedOrganisation} />
                    {(editing.allowedOrganisationSummaries ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {(editing.allowedOrganisationSummaries ?? []).map((organisation) => (
                          <Badge key={organisation.id} variant="secondary" className="gap-1">
                            {organisation.name ?? organisation.id}
                            <button type="button" onClick={() => removeAllowedOrganisation(organisation.id)}>
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Only these organisations see this listing in their marketplace. Removing one later does not
                      take away an agent it already has.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Monthly price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={editing.monthlyFee ?? 0}
                        onChange={(event) => setField("monthlyFee", parseFloat(event.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Discount (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        min={0}
                        max={100}
                        value={editing.discountPercent ?? 0}
                        onChange={(event) =>
                          setField("discountPercent", Math.min(100, Math.max(0, parseInt(event.target.value) || 0)))
                        }
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The monthly price is what each agent from this listing normally costs, in place of the tier price;
                    the discount is taken off it on the organisation's invoice, so it still sees the normal price. 0 makes
                    it free. Usage per minute follows the organisation's pricing plan.
                  </p>
                </>
              ) : (
                <p className="rounded-lg border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  Public listings charge the template's tier price per month and the organisation's plan rate per
                  minute. Both are set under Pricing, and the marketplace shows each organisation its own figures.
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editing.sortOrder ?? 0}
                    onChange={(event) => setField("sortOrder", parseInt(event.target.value) || 0)}
                  />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label>Featured</Label>
                  <Switch checked={editing.featured ?? false} onCheckedChange={(checked) => setField("featured", checked)} />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
